import { z } from 'zod';
import { categoriaDeAvisoSchema, severidadPercibidaSchema } from './avisos.js';
/**
 * Catálogo de eventos estructurados · la columna vertebral de datos del sistema.
 *
 * Regla de `CLAUDE.md` §9.3 / `dev/14-plan-de-comando.md` §9.3: **todo hecho
 * relevante es un evento append-only** con tenant, programa, tipo de un catálogo
 * cerrado y versionado, fase, origen y timestamp UTC. Los reportes N1 (M-15) y
 * N2 (AI-04) se calculan leyendo esta tabla, y nada más. Si un KPI no se puede
 * calcular desde acá, falta un tipo: se agrega al catálogo, no se hace una query
 * ad-hoc contra una tabla de estado.
 *
 * **El catálogo es cerrado en tres capas**, y las tres tienen que decir lo mismo:
 * este archivo (fuente de verdad), la tabla `clinical_event_type` de Postgres
 * —a la que apunta una FK, así que un tipo inventado no entra ni por SQL a
 * mano—, y `dev/catalogo-eventos.md`, que explica qué alimenta cada uno.
 *
 * **Modelo clínico vigente** (entrada 2026-08-11 de `CLAUDE.md` §8): semáforo de
 * 3 colores encendido sólo por las 4 escalas, sin motor de alertas, sin
 * escalamientos y sin avisos automáticos al equipo. Por eso **no hay ni puede
 * haber tipos de alerta en este catálogo**: el dolor genera reporte, el silencio
 * no avisa, y la salida segura cierra y registra. Un tipo `alert.*` acá sería
 * construir algo derogado.
 *
 * **Ningún payload lleva texto libre ni PII.** Sólo identificadores, códigos de
 * listas cerradas y valores numéricos. La narrativa vive donde corresponde —la
 * nota clínica en FHIR (D1), el motivo de cierre en el formulario M-13— y el
 * evento guarda la referencia. `emitEvent` además rechaza cualquier clave que la
 * lista de redacción considere personal.
 */
// ─────────────────────────────────────────────────────────────────────────────
// Versión del catálogo
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Versión del catálogo. Se incrementa **al agregar tipos** y al extender el
 * payload de uno que ya existe, nunca al quitar: un tipo emitido alguna vez no
 * se borra, se marca inactivo.
 *
 * La 3 suma a `silence.resolved` un campo opcional, `resueltoPorEventoId`: qué
 * contacto cerró el episodio, cuando lo cerró un contacto y no el barrido
 * (P-88). Una fila con versión 2 nunca lo trae, y por eso la versión importa:
 * sin ella, un reporte no podría distinguir "no lo resolvió un contacto" de "en
 * esa época no se registraba".
 *
 * La 4 suma a `silence.retried` el resultado `sin_canal` (H-17). Hasta acá ese
 * evento se guardaba con `resultado: 'enviado'` **aunque no exista ningún canal
 * de envío**: el envío es de E5 y hoy no sale ningún mensaje. En desarrollo no
 * dañaba; el riesgo era que llegara así a un ambiente con pacientes, porque el
 * registro de eventos es de **sólo agregar** y un dato falso escrito ahí no se
 * corrige nunca. Los eventos ya escritos **no se tocan**: los que dicen
 * 'enviado' con versión 3 o menos se leen sabiendo que en esa época el canal no
 * existía, que es exactamente para lo que sirve la versión.
 *
 * La 5 suma un tipo: `checkin.completed`, el hecho de que el contacto quedó
 * respondido (P-97). Hasta acá "realizado" no era un hecho sino una **resta**
 * —los intentados menos los perdidos—, y una resta esconde justamente el dato
 * que falta: nadie anotaba *cuándo* se respondió. El efecto se veía en la
 * Actividad reciente, que contaba un contacto recién al **cerrarse su ventana**:
 * una evaluación respondida hoy, con ventana de 120 horas, no sumaba hasta
 * dentro de cinco días. Es la regla de §9.3 aplicada al pie de la letra: si un
 * KPI no sale del catálogo, falta un tipo.
 *
 * La 6 extiende tres payloads del grupo A, que hasta acá existían y casi nadie
 * emitía (H-20). `auth.session_started` suma la vía `dispositivo_recordado`: el
 * paciente y el cuidador vuelven a entrar sin enlace durante 90 días (E3.24).
 * `auth.magic_link_used` suma la decisión `regenerado`: el equipo emitió un
 * enlace nuevo y éste quedó anulado sin que nadie lo usara. Y
 * `auth.login_failed` suma la vía del intento y el hash del agente, que es lo
 * que permite distinguir un error de tipeo de alguien probando contraseñas
 * sin guardar ningún dato de la persona.
 *
 * La 7 trae el alta con identidad cifrada (ADR-020, E2.3 y E2.5). Dos tipos del
 * grupo A: `patient.identity_checked`, la búsqueda de candidatos adentro de la
 * institución y de coincidencias en la red, que es la única lectura cruzada
 * entre instituciones del sistema y por eso deja rastro aunque nadie dé el
 * alta; y `patient.identity_resolved`, la decisión de quien da el alta sobre
 * cada candidato o aviso, con el puntaje y quién decidió. Ninguno lleva un dato
 * de identidad: ni un nombre ni una huella.
 *
 * La 8 trae los cuestionarios de hito (E3.25, D-01). `questionnaire.answered`
 * es el hecho de haber respondido uno: el paciente o su cuidador el de un hito,
 * o el equipo la medicación al egreso en el alta. Lleva cuántos medicamentos,
 * reinternaciones y reintervenciones declaró, nunca cuáles. Y
 * `clinical_fact.reported` es cada reinternación o reintervención que se
 * cuenta por primera vez, fechada el día en que pasó: es lo que el timeline del
 * equipo muestra como hecho y de lo que se deriva el total del año. Ninguno de
 * los dos mueve el semáforo (R-08).
 *
 * La 9 suma la vía `codigo_de_activacion` a `auth.session_started` y a
 * `auth.login_failed` (INGRESO-2): la app instalada en el iPhone entra con un
 * código de seis dígitos que la persona pidió desde su sesión abierta. Ninguno
 * de los dos lleva el código.
 *
 * Cada evento guarda la versión con la que se emitió (`catalogo_version`), así
 * un reporte que corre dentro de dos años sabe con qué diccionario leer filas
 * viejas sin adivinar.
 */
export const CATALOGO_VERSION = 9;
// ─────────────────────────────────────────────────────────────────────────────
// Origen y grupo de auditoría
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Quién produjo el hecho.
 *
 * `CLAUDE.md` §9.3 enumera cinco (paciente, PIA, motor, profesional, sistema).
 * Se agrega **`cuidador`**: el cuidador responde de forma estructurada a la
 * consulta del equipo (C-03, `wireframes-cuidador.html`) y eso es un evento
 * in-scope de M5. Con cinco valores habría que anotarlo como `paciente` —falso—
 * o como `sistema` —también falso—, y el origen dejaría de servir para filtrar
 * quién contestó. Registrado en la bitácora como desvío del prompt.
 */
export const origenEventoSchema = z.enum([
    'paciente',
    'cuidador',
    'pia',
    'motor',
    'profesional',
    'sistema',
]);
/**
 * Grupo del piso de auditoría de `arquitectura.html` (§ Auditoría y
 * trazabilidad). Los cuatro grupos son el mínimo que no puede faltar, y el
 * catálogo los cubre entero — la correspondencia ítem por ítem está en
 * `dev/catalogo-eventos.md` §4.
 */
export const grupoAuditoriaSchema = z.enum(['A', 'B', 'C', 'D']);
/**
 * Fases del programa (partición del protocolo de 12 meses).
 *
 * `EXTENSION` entra en la versión 2 del catálogo y **cierra la deuda anotada en
 * `catalogo-eventos.md` §8**: el programa puede continuar después de D+360 con
 * las características de consolidación (definición de Fundación del 06/08), y
 * hasta ahora un evento emitido durante esa continuación quedaba etiquetado como
 * `M12` —el cierre anual—, que no es donde estaba pasando. **No es un séptimo
 * cierre**: el programa sigue abierto y se cierra después, por alguno de los seis.
 */
export const faseProgramaSchema = z.enum(['M1_M3', 'M4_M6', 'M7_M11', 'M12', 'EXTENSION']);
// ─────────────────────────────────────────────────────────────────────────────
// Piezas comunes de payload
// ─────────────────────────────────────────────────────────────────────────────
const uuid = z.uuid();
const fechaHora = z.iso.datetime();
/** Los cuatro instrumentos, con el naming vigente (CAS-E oficial 0–6). */
export const instrumentoSchema = z.enum(['nms', 'cas_e', 'katz15', 'eq5d5l']);
/**
 * Colores del semáforo. Tres (definición Fundación 29/07, ratificada 06/08).
 *
 * `gris` entra como **candidato parametrizado y apagado por defecto**: Ezequiel
 * lo propuso el 6/8 como estado de *contactabilidad* —no un color clínico— y
 * falta que Fundación defina n intentos y ventana. Está en el enum para que
 * activarlo sea configuración y no una migración; hasta que la definición
 * llegue, ninguna regla lo escribe.
 */
export const colorSemaforoSchema = z.enum(['verde', 'amarillo', 'rojo', 'gris']);
/** Flecha de tendencia. Se mueve con un cambio de ≥1 punto entre mediciones. */
export const tendenciaSchema = z.enum(['sube', 'baja', 'estable']);
/** Los seis cierres (`closed_lost` incluye el "resultado desconocido"). */
export const tipoDeCierreSchema = z.enum([
    'closed_normal',
    'closed_deceased',
    'closed_voluntary',
    'closed_lost',
    'closed_early_discharge',
    'closed_referred',
]);
export const CATALOGO_DE_EVENTOS = {
    // ══ Grupo A · Acceso y seguridad ══════════════════════════════════════════
    'auth.session_started': {
        descripcion: 'Inicio de sesión, con el rol y la vía de acceso.',
        auditoria: 'A',
        origenes: ['sistema'],
        requierePrograma: false,
        desdeVersion: 1,
        // El "dispositivo y contexto" del piso de auditoría entra como HASH del
        // user agent, nunca como IP ni como el agente en claro: en la base
        // operativa serían datos personales (Ley 25.326). El hash alcanza para
        // decir "mismo dispositivo que la vez pasada", que es la pregunta real.
        payload: z.strictObject({
            rol: z.string(),
            via: z.enum(['magic_link', 'password', 'dispositivo_recordado', 'codigo_de_activacion']),
            mfa: z.boolean(),
            sesionId: uuid.optional(),
            agenteHash: z.string().length(64).optional(),
        }),
    },
    'auth.session_revoked': {
        descripcion: 'Fin de sesión: inactividad, cierre explícito o revocación.',
        auditoria: 'A',
        origenes: ['sistema', 'profesional'],
        requierePrograma: false,
        desdeVersion: 1,
        payload: z.strictObject({
            sesionId: uuid,
            motivo: z.enum(['inactividad', 'cierre_explicito', 'revocacion_administrativa']),
        }),
    },
    'auth.login_failed': {
        descripcion: 'Intento de acceso fallido o sospechoso.',
        auditoria: 'A',
        origenes: ['sistema'],
        requierePrograma: false,
        desdeVersion: 1,
        nivelLog: 'warn',
        // Sólo se registra cuando el intento ya resolvió a un tenant. Un intento
        // contra un tenant desconocido no tiene dónde guardarse sin romper RLS y
        // queda en Cloud Logging, que es donde vive lo pre-tenant.
        payload: z.strictObject({
            motivo: z.enum([
                'enlace_invalido',
                'enlace_vencido',
                'enlace_consumido',
                'credencial_invalida',
                'demasiados_intentos',
            ]),
            enlaceId: uuid.optional(),
            // Desde la 6. Por qué puerta fue el intento. Nunca el email ni el código:
            // la vía y el hash del agente alcanzan para ver un patrón.
            via: z
                .enum([
                'magic_link',
                'password',
                'segundo_factor',
                'dispositivo_recordado',
                'codigo_de_activacion',
            ])
                .optional(),
            agenteHash: z.string().length(64).optional(),
        }),
    },
    'auth.magic_link_used': {
        descripcion: 'Enlace de un solo uso consumido, aceptado o rechazado.',
        auditoria: 'A',
        origenes: ['sistema'],
        requierePrograma: false,
        desdeVersion: 1,
        payload: z.strictObject({
            enlaceId: uuid,
            tipo: z.enum([
                'ONBOARDING_PACIENTE',
                'ACTIVACION_CUIDADOR',
                'PRIMER_ACCESO_EQUIPO',
                'REINGRESO',
            ]),
            rol: z.string(),
            // `regenerado` desde la 6: el enlace no se usó, lo anuló uno nuevo.
            decision: z.enum(['aceptado', 'rechazado', 'regenerado']).optional(),
        }),
    },
    'iam.role_changed': {
        descripcion: 'Cambio de rol o de permisos, con autor y justificación.',
        auditoria: 'A',
        origenes: ['profesional', 'sistema'],
        requierePrograma: false,
        desdeVersion: 1,
        // La justificación entra codificada + referencia al registro que guarda la
        // narrativa (M8). Texto libre acá sería la vía más corta a un nombre propio
        // dentro de la base operativa.
        payload: z.strictObject({
            usuarioObjetivoId: uuid,
            rolAnterior: z.string().nullable(),
            rolNuevo: z.string(),
            motivo: z.enum(['alta', 'baja', 'cambio_de_funcion', 'correccion', 'licencia', 'otro']),
            justificacionRef: uuid.nullable(),
        }),
    },
    'iam.admin_action': {
        descripcion: 'Acción administrativa sensible sobre el tenant o sus usuarios.',
        auditoria: 'A',
        origenes: ['profesional', 'sistema'],
        requierePrograma: false,
        desdeVersion: 1,
        payload: z.strictObject({
            accion: z.enum([
                'tenant_activado',
                'tenant_suspendido',
                'usuario_activado',
                'usuario_desactivado',
                'mfa_reiniciado',
                'exportacion_generada',
            ]),
            objetoId: uuid.nullable(),
        }),
    },
    'iam.out_of_context_access': {
        descripcion: 'Acceso a un caso fuera del contexto habitual del actor.',
        auditoria: 'A',
        origenes: ['profesional'],
        requierePrograma: true,
        desdeVersion: 1,
        nivelLog: 'warn',
        payload: z.strictObject({
            motivo: z.enum(['no_asignado', 'fuera_de_horario', 'programa_cerrado']),
        }),
    },
    'patient.identity_checked': {
        descripcion: 'Búsqueda de la identidad en el alta: candidatos en la institución y en la red.',
        auditoria: 'A',
        origenes: ['profesional', 'sistema'],
        requierePrograma: false,
        desdeVersion: 7,
        // Cuántos, nunca quiénes: el rastro es que se buscó y qué se encontró, no
        // una lista de pacientes que vuelva a identificar a nadie.
        payload: z.strictObject({
            candidatos: z.number().int().nonnegative(),
            coincidenciasEnLaRed: z.number().int().nonnegative(),
        }),
    },
    'patient.identity_resolved': {
        descripcion: 'Decisión de quien da el alta ante un candidato o un aviso de la red.',
        auditoria: 'A',
        origenes: ['profesional', 'sistema'],
        requierePrograma: true,
        desdeVersion: 7,
        // El programa del evento es el que nace con el alta. `candidatoUsuarioId`
        // sólo en la institución: en la red el otro paciente es de otra
        // institución y su id no tiene nada que hacer acá.
        payload: z.strictObject({
            ambito: z.enum(['institucion', 'red']),
            decision: z.enum(['misma_persona', 'otra_persona']),
            puntaje: z.number().int().min(0).max(100).nullable(),
            candidatoUsuarioId: uuid.nullable(),
        }),
    },
    // ══ Grupo B · Operación del caso · onboarding ═════════════════════════════
    'onboarding.started': {
        descripcion: 'El paciente abrió su enlace y arrancó el onboarding.',
        auditoria: 'B',
        origenes: ['sistema'],
        requierePrograma: true,
        desdeVersion: 1,
        payload: z.strictObject({ usuarioId: uuid }),
    },
    'onboarding.step_completed': {
        descripcion: 'Paso del onboarding completado. Es el embudo (P-01 a P-02d).',
        auditoria: 'B',
        origenes: ['paciente', 'cuidador'],
        requierePrograma: true,
        desdeVersion: 1,
        payload: z.strictObject({
            paso: z.enum(['identidad', 'consentimiento', 'cuidador', 'permisos']),
            siguiente: z.enum(['consentimiento', 'cuidador', 'permisos', 'listo']),
        }),
    },
    'onboarding.caregiver_invited': {
        descripcion: 'Invitación al cuidador emitida (P-02c).',
        auditoria: 'B',
        origenes: ['paciente'],
        requierePrograma: true,
        desdeVersion: 1,
        // Sin teléfono: el número se usa para emitir y no se persiste (E2.5).
        payload: z.strictObject({
            enlaceId: uuid,
            relacion: z.string().max(40).nullable(),
        }),
    },
    'onboarding.abandoned': {
        descripcion: 'Onboarding sin avance más allá del umbral de recontacto (§A).',
        auditoria: 'B',
        origenes: ['sistema'],
        requierePrograma: true,
        desdeVersion: 1,
        payload: z.strictObject({
            paso: z.string(),
            minutosSinAvanzar: z.number().int().nonnegative(),
        }),
    },
    'onboarding.completed': {
        descripcion: 'Onboarding terminado: el programa queda operativo.',
        auditoria: 'B',
        origenes: ['paciente', 'cuidador'],
        requierePrograma: true,
        desdeVersion: 1,
        payload: z.strictObject({
            completadoPor: z.enum(['paciente', 'cuidador']),
            pushHabilitado: z.boolean(),
        }),
    },
    'consent.signed': {
        descripcion: 'Consentimiento informado firmado, con versión y hash del texto.',
        auditoria: 'B',
        origenes: ['paciente', 'cuidador'],
        requierePrograma: true,
        desdeVersion: 1,
        payload: z.strictObject({
            consentRecordId: uuid,
            version: z.string(),
            textoHash: z.string().length(64),
            otorgadoPor: z.string(),
            otorgadoAt: fechaHora,
            fhirStore: z.string(),
        }),
    },
    'consent.revoked': {
        descripcion: 'Revocación del consentimiento. Hecho nuevo, no corrección.',
        auditoria: 'B',
        origenes: ['paciente', 'cuidador', 'profesional'],
        requierePrograma: true,
        desdeVersion: 1,
        payload: z.strictObject({
            consentRecordId: uuid,
            revocadoAt: fechaHora,
        }),
    },
    // ══ Grupo B · Operación del caso · programa ═══════════════════════════════
    'program.started': {
        descripcion: 'Alta del programa de 365 días.',
        auditoria: 'B',
        origenes: ['profesional', 'sistema'],
        requierePrograma: true,
        desdeVersion: 1,
        // `reingreso` cubre el caso del paciente que vuelve tras un abandono: en
        // fase 1 arranca desde el día 1 con un programa nuevo (CLAUDE.md §4.4, el
        // modelo multi-episodio formal está diferido).
        payload: z.strictObject({
            cirugiaAt: z.iso.date().nullable(),
            reingreso: z.boolean(),
        }),
    },
    'program.phase_changed': {
        descripcion: 'Transición de fase del protocolo (M1-M3 · M4-M6 · M7-M11 · M12).',
        auditoria: 'B',
        origenes: ['motor'],
        requierePrograma: true,
        desdeVersion: 1,
        payload: z.strictObject({
            faseAnterior: faseProgramaSchema,
            faseNueva: faseProgramaSchema,
            diaDelPrograma: z.number().int().nonnegative(),
        }),
    },
    'program.intensity_adjusted': {
        descripcion: 'Ajuste de intensidad del seguimiento por el profesional (UC-M08).',
        auditoria: 'C',
        origenes: ['profesional'],
        requierePrograma: true,
        desdeVersion: 1,
        payload: z.strictObject({
            patronAnterior: z.string().max(40),
            patronNuevo: z.string().max(40),
            motivo: z.enum([
                'buena_evolucion',
                'desvio_relevante',
                'decision_clinica',
                'pedido_paciente',
            ]),
            temporal: z.boolean(),
            hastaAt: fechaHora.nullable(),
        }),
    },
    'program.continuity_decided': {
        descripcion: 'Decisión de continuidad tras la teleconsulta de hito.',
        auditoria: 'C',
        origenes: ['profesional'],
        requierePrograma: true,
        desdeVersion: 1,
        payload: z.strictObject({
            decision: z.enum(['continuar', 'extender', 'derivar', 'alta_anticipada']),
            hito: z.string().max(20),
        }),
    },
    'program.extended': {
        descripcion: 'Extensión post-M12 con las características de consolidación.',
        auditoria: 'B',
        origenes: ['profesional'],
        requierePrograma: true,
        desdeVersion: 1,
        payload: z.strictObject({
            decididoAt: fechaHora,
            duracionDias: z.number().int().positive(),
        }),
    },
    'program.closed': {
        descripcion: 'Cierre del programa. Uno de los seis tipos (M-13).',
        auditoria: 'B',
        origenes: ['profesional', 'sistema'],
        requierePrograma: true,
        desdeVersion: 1,
        // El motivo narrativo vive en el formulario de cierre y en FHIR; acá va el
        // código estructurado, que es lo que cuentan los reportes. `unknown_outcome`
        // es el matiz que definió Fundación el 6/8 para `closed_lost`.
        payload: z.strictObject({
            tipo: tipoDeCierreSchema,
            motivoCodigo: z.string().max(40).nullable(),
            diaDelPrograma: z.number().int().nonnegative(),
        }),
    },
    // ══ Grupo B · Semáforo y tendencia ════════════════════════════════════════
    'case_status.changed': {
        descripcion: 'Cambio de semáforo. Sólo lo escribe el motor, sólo por escalas.',
        auditoria: 'B',
        origenes: ['motor'],
        requierePrograma: true,
        desdeVersion: 1,
        // Invariante D4: el color sale de las 4 escalas y de nada más. Por eso el
        // payload EXIGE decir qué instrumento lo encendió — un cambio de color sin
        // instrumento no se puede emitir.
        payload: z.strictObject({
            colorAnterior: colorSemaforoSchema.nullable(),
            colorNuevo: colorSemaforoSchema,
            instrumento: instrumentoSchema,
            puntaje: z.number(),
            reglaId: z.string().max(60),
        }),
    },
    'case_trend.changed': {
        descripcion: 'Cambio de tendencia (↑ ↓ →) por variación de ≥1 punto.',
        auditoria: 'B',
        origenes: ['motor'],
        requierePrograma: true,
        desdeVersion: 1,
        payload: z.strictObject({
            instrumento: instrumentoSchema,
            tendencia: tendenciaSchema,
            puntajeAnterior: z.number(),
            puntajeNuevo: z.number(),
        }),
    },
    // ══ Grupo B · Cadencia y captura del seguimiento ══════════════════════════
    'checkin.scheduled': {
        descripcion: 'Ítem del plan materializado para hoy. Es el denominador de adherencia.',
        auditoria: 'B',
        origenes: ['motor'],
        requierePrograma: true,
        desdeVersion: 1,
        payload: z.strictObject({
            itemId: uuid,
            clase: z.enum(['microcheck', 'escala', 'teleconsulta']),
            hito: z.string().max(20).nullable(),
            venceAt: fechaHora,
        }),
    },
    'checkin.notified': {
        descripcion: 'Aviso enviado al paciente por su canal (ventana 09–11 local).',
        auditoria: 'B',
        origenes: ['sistema'],
        requierePrograma: true,
        desdeVersion: 1,
        payload: z.strictObject({
            itemId: uuid,
            canal: z.enum(['push', 'whatsapp']),
            reintento: z.number().int().nonnegative(),
        }),
    },
    'checkin.missed': {
        descripcion: 'Ítem vencido sin respuesta. Con `scheduled`, da el ratio de adherencia.',
        auditoria: 'B',
        origenes: ['motor'],
        requierePrograma: true,
        desdeVersion: 1,
        payload: z.strictObject({
            itemId: uuid,
            clase: z.enum(['microcheck', 'escala', 'teleconsulta']),
        }),
    },
    'checkin.completed': {
        descripcion: 'Contacto dado por respondido por el servidor. Es el numerador de adherencia.',
        auditoria: 'B',
        origenes: ['motor'],
        requierePrograma: true,
        desdeVersion: 5,
        // **No es un contacto.** El contacto ya lo anotó el evento del paciente que
        // lo provocó —`microcheck.answered` o `scale.completed`—, y es ése el que
        // mueve `ultimo_contacto_at` y resuelve el silencio. Éste es la consecuencia
        // que saca el servidor: el ítem del plan quedó cerrado por respuesta. Por eso
        // el origen es `motor` y no `paciente`: si fuera del paciente, un mismo
        // contacto se contaría dos veces y el silencio se evaluaría dos veces.
        //
        // `tardio` es el mismo valor que `llegoTarde` decidió para la aplicación que
        // lo cerró, así el ratio y la puntualidad se leen del mismo hecho.
        payload: z.strictObject({
            itemId: uuid,
            clase: z.enum(['microcheck', 'escala', 'teleconsulta']),
            hito: z.string().max(20).nullable(),
            tardio: z.boolean(),
        }),
    },
    'microcheck.answered': {
        descripcion: 'Microcheck respondido, con la duración y si llegó tarde.',
        auditoria: 'B',
        origenes: ['paciente', 'cuidador'],
        requierePrograma: true,
        desdeVersion: 1,
        // Las respuestas en sí van a FHIR (QuestionnaireResponse) y al evento de
        // cada variable; acá queda el hecho de haber respondido, que es lo que
        // cuentan adherencia y "días desde el último contacto".
        payload: z.strictObject({
            itemId: uuid.nullable(),
            preguntasRespondidas: z.number().int().nonnegative(),
            preguntasTotales: z.number().int().positive(),
            duracionSegundos: z.number().int().nonnegative(),
            tardio: z.boolean(),
            via: z.enum(['texto', 'voz', 'mixto']),
        }),
    },
    'scale.completed': {
        descripcion: 'Escala de hito completada, con puntaje y hito. Base de M-15.',
        auditoria: 'B',
        origenes: ['paciente', 'cuidador', 'profesional'],
        requierePrograma: true,
        desdeVersion: 1,
        // `hito` es lo que arma las 8 columnas de la tabla de evolución de M-15 y
        // la comparativa entre cohortes de AI-04 ("ganancia ≥1 punto en NMS al M6
        // vs el baseline D+28"): sin él, los puntajes no se pueden alinear.
        payload: z.strictObject({
            instrumento: instrumentoSchema,
            hito: z.string().max(20),
            puntaje: z.number(),
            subpuntajes: z.record(z.string().max(30), z.number()).optional(),
            duracionSegundos: z.number().int().nonnegative(),
            tardio: z.boolean(),
            questionnaireResponseId: z.string().max(64).nullable(),
        }),
    },
    'scale.partial': {
        descripcion: 'Escala respondida a medias. Cuenta como intentada, no penaliza.',
        auditoria: 'B',
        origenes: ['paciente', 'cuidador', 'profesional'],
        requierePrograma: true,
        desdeVersion: 1,
        payload: z.strictObject({
            instrumento: instrumentoSchema,
            hito: z.string().max(20),
            itemsRespondidos: z.number().int().nonnegative(),
            itemsTotales: z.number().int().positive(),
        }),
    },
    'scale.abandoned': {
        descripcion: 'Escala abandonada sin retomar dentro de la ventana.',
        auditoria: 'B',
        origenes: ['paciente', 'cuidador', 'motor'],
        requierePrograma: true,
        desdeVersion: 1,
        payload: z.strictObject({
            instrumento: instrumentoSchema,
            hito: z.string().max(20),
            itemsRespondidos: z.number().int().nonnegative(),
            itemsTotales: z.number().int().positive(),
        }),
    },
    'questionnaire.answered': {
        descripcion: 'Cuestionario de hito respondido: medicación, reinternaciones, autonomía (D-01).',
        auditoria: 'B',
        // El profesional es la medicación al egreso, que se carga en el alta; el
        // sistema, la misma carga cuando la hace un alta sin persona detrás (el
        // seed de demo, el arnés de escenarios).
        origenes: ['paciente', 'cuidador', 'profesional', 'sistema'],
        requierePrograma: true,
        desdeVersion: 8,
        // Las respuestas van a FHIR (QuestionnaireResponse, MedicationStatement,
        // Encounter y Procedure) y a la aplicación en Postgres; acá queda el hecho
        // de haber respondido y cuánto declaró. Ningún nombre de droga ni fecha:
        // son códigos y conteos, como todo payload del catálogo.
        payload: z.strictObject({
            cuestionario: z.string().min(1).max(30),
            hito: z.string().max(20).nullable(),
            itemId: uuid.nullable(),
            preguntasRespondidas: z.number().int().nonnegative(),
            preguntasTotales: z.number().int().positive(),
            duracionSegundos: z.number().int().nonnegative(),
            tardio: z.boolean(),
            /** `null` si el cuestionario no pregunta la medicación (D+240). */
            medicamentos: z.number().int().nonnegative().nullable(),
            reinternaciones: z.number().int().nonnegative(),
            reintervenciones: z.number().int().nonnegative(),
        }),
    },
    'clinical_fact.reported': {
        descripcion: 'Reinternación o reintervención contada por primera vez, con su fecha.',
        auditoria: 'B',
        origenes: ['paciente', 'cuidador'],
        requierePrograma: true,
        desdeVersion: 8,
        // `occurredAt` es el día en que pasó, no el día en que se contó: es lo que
        // ordena el hecho en la historia del caso. El contacto del paciente lo anota
        // `questionnaire.answered`, con la hora del servidor. Si el mismo hecho
        // vuelve a contarse en otro cuestionario (las ventanas del protocolo se
        // pisan), no se emite de nuevo.
        payload: z.strictObject({
            hecho: z.enum(['reinternacion', 'reintervencion']),
            fecha: z.iso.date(),
            /** Sólo en la reinternación. */
            lugar: z.enum(['esta-institucion', 'otra-institucion']).nullable(),
            /** Sólo en la reintervención: el código de cirugía del RFC. */
            cirugia: z.string().max(40).nullable(),
            cuestionario: z.string().min(1).max(30),
            hito: z.string().max(20).nullable(),
        }),
    },
    'pain.reported': {
        descripcion: 'Reporte de dolor 0–10. Genera REPORTE al equipo, nunca aviso.',
        auditoria: 'B',
        origenes: ['paciente', 'cuidador', 'profesional'],
        requierePrograma: true,
        desdeVersion: 1,
        // Definición de Fundación (29/07, ratificada 06/08): el dolor queda FUERA
        // del semáforo. Se sigue preguntando, genera un reporte al equipo
        // asistencial y una recomendación al paciente y al cuidador; no notifica
        // con urgencia y no pinta color. Las reglas "dolor ≥7 → alerta roja" del
        // protocolo están derogadas por escrito.
        payload: z.strictObject({
            valor: z.number().int().min(0).max(10),
            contexto: z.enum(['microcheck', 'escala', 'reporte_espontaneo', 'consulta']),
            observationId: z.string().max(64).nullable(),
        }),
    },
    'patient.event_reported': {
        descripcion: 'Evento o síntoma reportado por el paciente de forma estructurada (UC-P06).',
        auditoria: 'B',
        origenes: ['paciente'],
        requierePrograma: true,
        desdeVersion: 1,
        // Categoría de lista cerrada, sin la descripción del paciente: la narrativa
        // no entra a la base operativa, y estructurarla automáticamente es
        // justamente lo diferido en CLAUDE.md §4.1.
        // Desde E3.25 (D-03) además se proyecta al registro clínico como
        // `Observation`: el payload no cambia, así que el catálogo no sube de versión.
        payload: z.strictObject({
            categoria: categoriaDeAvisoSchema,
            severidadPercibida: severidadPercibidaSchema.nullable(),
        }),
    },
    'caregiver.response_recorded': {
        descripcion: 'Respuesta estructurada del cuidador a la consulta del equipo (C-03).',
        auditoria: 'B',
        origenes: ['cuidador'],
        requierePrograma: true,
        desdeVersion: 1,
        payload: z.strictObject({
            consultaId: uuid,
            opcion: z.string().max(40),
        }),
    },
    // ══ Grupo B · Protocolo de silencio ═══════════════════════════════════════
    'silence.detected': {
        descripcion: 'Silencio detectado según el umbral de la fase. Sin aviso al equipo.',
        auditoria: 'B',
        origenes: ['motor'],
        requierePrograma: true,
        desdeVersion: 1,
        payload: z.strictObject({
            horasSinRespuesta: z.number().int().nonnegative(),
            umbralHoras: z.number().int().positive(),
        }),
    },
    'silence.retried': {
        descripcion: 'Nivel del protocolo ejecutado: PIA insiste (1) o se escribe al contacto (2).',
        auditoria: 'B',
        origenes: ['motor', 'sistema'],
        requierePrograma: true,
        desdeVersion: 1,
        // No hay nivel 3: el aviso al equipo se eliminó (definición 2026-08).
        payload: z.strictObject({
            nivel: z.union([z.literal(1), z.literal(2)]),
            canal: z.enum(['pia', 'push', 'whatsapp']),
            /**
             * Qué pasó con el envío.
             *
             * `sin_canal` desde la versión 4 del catálogo (H-17), y es el que
             * corresponde hoy: **no existe ningún canal de envío**. Hasta acá se
             * guardaba `enviado`, que era falso en un registro que no se puede
             * corregir. `enviado` y `fallido` los va a poner el canal de verdad
             * cuando E5 lo construya: el nivel se ejecutó igual —el protocolo avanzó,
             * el episodio cuenta— y lo que no ocurrió es la salida del mensaje.
             */
            resultado: z.enum(['enviado', 'fallido', 'sin_canal']),
        }),
    },
    'silence.exhausted': {
        descripcion: 'Los dos niveles del protocolo de silencio se agotaron sin respuesta.',
        auditoria: 'B',
        origenes: ['motor'],
        requierePrograma: true,
        desdeVersion: 2,
        // **No es un tercer nivel.** El protocolo tiene dos —PIA insiste, después se
        // le escribe a la persona de contacto— y acá se deja de insistir: el hecho
        // queda registrado y nadie recibe un aviso. Es el candidato al estado `gris`
        // de contactabilidad que Ezequiel propuso el 6/8; falta que Fundación defina
        // n intentos y ventana, así que ninguna regla lo pinta todavía.
        payload: z.strictObject({
            horasEnSilencio: z.number().int().nonnegative(),
            nivelesEjecutados: z.literal(2),
        }),
    },
    'silence.resolved': {
        descripcion: 'El paciente volvió a responder tras el silencio.',
        auditoria: 'B',
        origenes: ['motor'],
        requierePrograma: true,
        desdeVersion: 1,
        payload: z.strictObject({
            horasEnSilencio: z.number().int().nonnegative(),
            nivelMaximoEjecutado: z.union([z.literal(0), z.literal(1), z.literal(2)]),
            /**
             * Qué contacto lo resolvió, cuando lo resolvió un contacto y no el
             * barrido diario (P-88, versión 3 del catálogo).
             *
             * Opcionales porque el barrido sigue pudiendo resolver por su cuenta —una
             * respuesta que entró por un camino que todavía no emite evento, o un
             * episodio que se cierra al recalcular—, y porque las filas anteriores a
             * la versión 3 no los tienen.
             */
            resueltoPorEventoId: z.string().uuid().optional(),
        }),
    },
    // ══ Grupo C · Intervención clínica ════════════════════════════════════════
    'intervention.recorded': {
        descripcion: 'Intervención del equipo sobre el caso, con actor y timestamp.',
        auditoria: 'C',
        origenes: ['profesional'],
        requierePrograma: true,
        desdeVersion: 1,
        payload: z.strictObject({
            tipo: z.enum([
                'llamado',
                'mensaje',
                'ajuste_terapeutico',
                'derivacion',
                'contacto_cuidador',
                'otro',
            ]),
            resultado: z.enum(['contactado', 'sin_respuesta', 'reprogramado']),
        }),
    },
    'clinical_note.recorded': {
        descripcion: 'Nota clínica creada o modificada. El texto vive en FHIR (D1).',
        auditoria: 'C',
        origenes: ['profesional'],
        requierePrograma: true,
        desdeVersion: 1,
        payload: z.strictObject({
            notaId: uuid,
            documentReferenceId: z.string().max(64).nullable(),
            operacion: z.enum(['creada', 'modificada']),
        }),
    },
    'teleconsult.recorded': {
        descripcion: 'Teleconsulta o consulta registrada, con actor y timestamp.',
        auditoria: 'C',
        origenes: ['profesional'],
        requierePrograma: true,
        desdeVersion: 1,
        payload: z.strictObject({
            citaId: uuid,
            hito: z.string().max(20).nullable(),
            estado: z.enum(['realizada', 'no_asistio', 'interrumpida', 'cancelada']),
            duracionMinutos: z.number().int().nonnegative().nullable(),
            encounterId: z.string().max(64).nullable(),
        }),
    },
    // ══ Grupo D · Sistema e IA ════════════════════════════════════════════════
    'pia.message': {
        descripcion: 'Turno de conversación paciente↔PIA. Indicador BID comprometido.',
        auditoria: 'D',
        origenes: ['paciente', 'pia'],
        requierePrograma: true,
        desdeVersion: 1,
        // El contenido NO viaja: al equipo llega resumen jerarquizado, nunca
        // transcripción cruda (pia.html §11.2). Acá queda la métrica de uso, que es
        // lo que el indicador BID pide: cuántas idas y vueltas, iniciadas por quién.
        payload: z.strictObject({
            conversacionId: uuid,
            turno: z.number().int().positive(),
            iniciadaPor: z.enum(['paciente', 'sistema']),
            caracteres: z.number().int().nonnegative(),
            via: z.enum(['texto', 'voz']),
            usoCorpus: z.boolean(),
        }),
    },
    'pia.safe_exit': {
        descripcion: 'Salida segura activada: cierra la conversación y queda registrada.',
        auditoria: 'D',
        origenes: ['pia'],
        requierePrograma: true,
        desdeVersion: 1,
        // Detección por lista cerrada de patrones, no por interpretación semántica.
        // No genera aviso (no existen) y NO escribe el semáforo (C-17).
        payload: z.strictObject({
            conversacionId: uuid,
            categoria: z.enum([
                'ideacion_suicida',
                'caida_grave',
                'dolor_toracico',
                'confusion_aguda',
                'sangrado_abundante',
                'fiebre_alta_sostenida',
            ]),
            patronId: z.string().max(60),
            copyVersion: z.string().max(20),
        }),
    },
    'pia.uncertain': {
        descripcion: 'PIA sin contenido suficiente en el corpus: deriva suave.',
        auditoria: 'D',
        origenes: ['pia'],
        requierePrograma: true,
        desdeVersion: 1,
        payload: z.strictObject({
            conversacionId: uuid,
            confianza: z.number().min(0).max(1),
            chunksRecuperados: z.number().int().nonnegative(),
        }),
    },
    'pia.out_of_scope': {
        descripcion: 'Consulta fuera del alcance del programa. Registro para análisis.',
        auditoria: 'D',
        origenes: ['pia'],
        requierePrograma: true,
        desdeVersion: 1,
        payload: z.strictObject({
            conversacionId: uuid,
            categoria: z.enum(['otra_patologia', 'administrativo', 'no_medico', 'indefinido']),
        }),
    },
    'pia.signal_to_engine': {
        descripcion: 'Señal derivada de PIA que alimenta el motor de reglas.',
        auditoria: 'D',
        origenes: ['pia'],
        requierePrograma: true,
        desdeVersion: 1,
        reservado: {
            motivo: 'Diferido por CLAUDE.md §4.1 (interpretación de lenguaje libre) y prohibido por el ' +
                'invariante D4: el semáforo corre sobre datos ciertos, no sobre inferencia de IA. ' +
                'El tipo existe con nombre para que reactivarlo por adenda sea sacar la reserva.',
        },
        payload: z.strictObject({
            conversacionId: uuid,
            senal: z.string().max(60),
        }),
    },
    'rule.applied': {
        descripcion: 'Regla del motor aplicada, con su contexto y resultado.',
        auditoria: 'D',
        origenes: ['motor'],
        requierePrograma: true,
        desdeVersion: 1,
        payload: z.strictObject({
            reglaId: z.string().max(60),
            reglaVersion: z.number().int().positive(),
            resultado: z.enum(['aplicada', 'sin_efecto']),
            disparadorEventoId: uuid.nullable(),
        }),
    },
    'rule_config.changed': {
        descripcion: 'Cambio en la configuración de reglas del tenant. Es lógica clínica.',
        auditoria: 'D',
        origenes: ['profesional', 'sistema'],
        requierePrograma: false,
        desdeVersion: 1,
        payload: z.strictObject({
            reglaId: z.string().max(60),
            versionAnterior: z.number().int().nonnegative().nullable(),
            versionNueva: z.number().int().positive(),
            campo: z.string().max(40),
            valorAnterior: z.string().max(60).nullable(),
            valorNuevo: z.string().max(60),
        }),
    },
    'corpus.changed': {
        descripcion: 'Alta, edición, aprobación o retiro de un documento del corpus RAG.',
        auditoria: 'D',
        origenes: ['profesional', 'sistema'],
        requierePrograma: false,
        desdeVersion: 1,
        // `archivoId` y no `documentoId`: la lista de redacción tapa toda clave que
        // contenga "documento" —por el DNI— y `emitEvent` la aplica en runtime. El
        // corpus se versiona por archivo (`contenidos-rag.html`), así que el nombre
        // fiel al dominio también es el que pasa.
        payload: z.strictObject({
            archivoId: uuid,
            version: z.number().int().positive(),
            estado: z.enum([
                'borrador',
                'en_revision',
                'aprobado',
                'publicado',
                'en_revision_por_actualizacion',
                'retirado',
            ]),
            operacion: z.enum(['creado', 'editado', 'estado_cambiado', 'eliminado']),
        }),
    },
    'fhir.write_failed': {
        descripcion: 'Escritura al FHIR store agotada: hay dato clínico sin proyectar (A-08).',
        auditoria: 'D',
        origenes: ['sistema'],
        requierePrograma: false,
        desdeVersion: 1,
        // A stderr: es lo que levanta Error Reporting, y un dato clínico que no
        // llegó al store necesita que alguien lo mire, no que quede en un tablero.
        nivelLog: 'error',
        payload: z.strictObject({
            recursoTipo: z.string().max(40),
            origenTabla: z.string().max(60),
            origenId: uuid,
            estado: z.number().int().nullable(),
            intentos: z.number().int().positive(),
        }),
    },
};
export const TIPOS_DE_EVENTO = Object.keys(CATALOGO_DE_EVENTOS);
export const tipoDeEventoSchema = z.enum(TIPOS_DE_EVENTO);
export function definicionDeEvento(tipo) {
    return CATALOGO_DE_EVENTOS[tipo];
}
export function esTipoDeEvento(valor) {
    return Object.hasOwn(CATALOGO_DE_EVENTOS, valor);
}
/**
 * Valida el payload contra el schema del tipo. Devuelve el valor parseado.
 *
 * Tira `ZodError` si no corresponde: un payload que no encaja es un error de
 * programación, no una entrada de usuario, y tiene que romper fuerte y temprano.
 */
export function validarPayloadDeEvento(tipo, payload) {
    return CATALOGO_DE_EVENTOS[tipo].payload.parse(payload);
}
//# sourceMappingURL=eventos.js.map